<template>
  <div class="provider-applications-active">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <generic-error v-else-if="hasError" data-testid="provider-applications-active-error" />
    <template v-else>
      <div
        v-if="showFilters"
        class="mb-16 flex flex-wrap gap-8 transition-opacity"
        data-testid="provider-applications-active-filter-row"
        :class="{ 'pointer-events-none opacity-60': applyingFilters }"
      >
        <filter-token
          :button-label="careTypeButtonLabel"
          :selected="careTypeFilter.isApplied"
          modal-label="Care type"
          class="!my-0"
        >
          <template #default="{ closeModal }">
            <radio-list-filter-panel
              :options="careTypeFilterOptions"
              :selected-value="careTypeFilterSelectedValue"
              radio-group-name="Filter by care type"
              :close-modal="closeModal"
              @filter-applied="onCareTypeFilterApplied"
            />
          </template>
        </filter-token>
      </div>
      <div v-if="applyingFilters" data-testid="applying-filters-skeleton" role="status" aria-live="polite" aria-busy="true">
        <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
      </div>
      <div v-else-if="hasApplications" data-testid="provider-applications-active-application-list">
        <ProviderApplicationCard
          v-for="application in applications"
          :key="application.id"
          :application="application"
          :display-as-active="true"
          class="mb-16"
        />
        <Button
          v-if="hasNextPage"
          variant="secondary"
          class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
          :loading="loadingMore"
          @click.prevent="loadMore"
        >
          View More
        </Button>
      </div>
      <div v-else-if="showFilters" data-testid="provider-applications-active-empty-filtered">
        <h2 class="text-h4 mb-8">No Applications Found</h2>
        <p class="text-body">Try updating your filters</p>
      </div>
      <div v-else data-testid="provider-applications-active-empty-default">
        <Card class="w-full">
          <h2 class="text-h4">Apply to your next job</h2>
          <img src="../../../images/applications/applications-empty-state.svg" alt="No jobs" class="w-full h-auto mt-16" />
          <Button class="w-full mt-16" variant="secondary" :to="findJobsUrl">Find Jobs</Button>
        </Card>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Card from '@/components/card/Card.vue';
import Button from '@/components/button/Button.vue';
import FilterToken from '@/components/filter_token/FilterToken.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import RadioListFilterPanel from '@/components/radioButtonGroup/RadioListFilterPanel.vue';
import ProviderApplicationCard from './ProviderApplicationCard.vue';
import { FETCH_PROVIDER_OPEN_APPLICATIONS } from '@/graphql/account/queries';
import { getSitterJobsURL } from '@/helpers/bookings/bookingsUrlHelper';
import { useUrlQueryParamFilter } from '@/composables/useUrlQueryParamFilter';
import {
  CARE_TYPE_FILTER_ANY,
  CARE_TYPE_FILTER_OPTIONS,
  careTypeFilterQueryOptions,
  careTypeButtonLabelFor,
} from './careTypeFilter';

export default defineComponent({
  name: 'ProviderApplicationsActive',
  components: { Card, Button, FilterToken, GenericError, RadioListFilterPanel, ProviderApplicationCard },
  setup() {
    const careTypeFilter = useUrlQueryParamFilter('careType', careTypeFilterQueryOptions);

    return { careTypeFilter };
  },
  apollo: {
    viewer: {
      query: FETCH_PROVIDER_OPEN_APPLICATIONS,
      variables(): { careType: string | null } {
        return {
          careType: this.careTypeFilter.value,
        };
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      result() {
        this.loading = false;
        this.applyingFilters = false;

        if (!this.viewer?.jobApplications) {
          this.hasError = true;
          return;
        }

        this.hasError = false;
        this.totalCount = this.viewer.jobApplications.totalCount ?? 0;
        this.lastPageInfo = this.viewer.jobApplications.pageInfo ?? null;
        this.applications = (this.viewer.jobApplications.edges ?? []).map((edge: { node: any }) => edge?.node).filter(Boolean);
        this.unfilteredTotalCount = this.viewer.totalOpenApplications?.totalCount ?? 0;
      },
      error() {
        this.loading = false;
        this.applyingFilters = false;
        if (!this.viewer?.jobApplications) {
          this.hasError = true;
        }
      },
    },
  },
  data(): {
    totalCount: number;
    unfilteredTotalCount: number;
    applications: any[];
    loading: boolean;
    loadingMore: boolean;
    applyingFilters: boolean;
    hasError: boolean;
    viewer: any;
    lastPageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
    careTypeFilterOptions: { value: string; label: string }[];
  } {
    return {
      totalCount: 0,
      unfilteredTotalCount: 0,
      applications: [],
      loading: true,
      loadingMore: false,
      applyingFilters: false,
      hasError: false,
      viewer: null,
      lastPageInfo: null,
      careTypeFilterOptions: CARE_TYPE_FILTER_OPTIONS,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    findJobsUrl(): string {
      return getSitterJobsURL(this.account?.careType ?? '');
    },
    hasApplications(): boolean {
      return this.totalCount > 0;
    },
    hasNextPage(): boolean {
      return !!this.lastPageInfo?.hasNextPage;
    },
    hasFiltersApplied(): boolean {
      return this.careTypeFilter.isApplied;
    },
    showFilters(): boolean {
      return this.unfilteredTotalCount > 0;
    },
    careTypeFilterSelectedValue(): string {
      return this.careTypeFilter.value ?? CARE_TYPE_FILTER_ANY;
    },
    careTypeButtonLabel(): string {
      return careTypeButtonLabelFor(this.careTypeFilter.value);
    },
  },
  methods: {
    resetList(): void {
      this.applications = [];
      this.lastPageInfo = null;
      this.totalCount = 0;
    },
    onCareTypeFilterApplied({ value }: { value: string }): void {
      const next = value === CARE_TYPE_FILTER_ANY ? null : value;
      if (this.careTypeFilter.apply(next)) {
        this.resetList();
        this.applyingFilters = true;
      }
    },
    getLastCursor(): string | null {
      return this.lastPageInfo?.endCursor ?? null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore || this.applyingFilters) return;

      this.loadingMore = true;

      try {
        const { data } = await this.$apollo.query({
          query: FETCH_PROVIDER_OPEN_APPLICATIONS,
          variables: {
            after,
            careType: this.careTypeFilter.value,
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        });
        const connection = data?.viewer?.jobApplications;
        this.lastPageInfo = connection?.pageInfo ?? null;
        const newNodes = (connection?.edges ?? []).map((edge: { node: any }) => edge?.node).filter(Boolean);
        this.applications = [...this.applications, ...newNodes];
      } catch (error) {
        console.error('Error loading more open applications:', error);
      } finally {
        this.loadingMore = false;
      }
    },
  },
});
</script>
